// Reminders — persisted on device, mirrored to local notifications (native).

import { create } from 'zustand';

import { getDb, TABLES } from '@/db';
import { syncReminderNotifications } from '@/notifications/local';
import type { Reminder } from '@/types/domain';
import { daysUntil } from '@/utils/format';

/** Pure helper (safe under React Compiler memoization — see goals-store note) */
export function upcomingOf(reminders: Reminder[], maxDays: number): Reminder[] {
  return reminders
    .filter((r) => r.enabled && daysUntil(r.eventDate) >= 0 && daysUntil(r.eventDate) <= maxDays)
    .sort((a, b) => daysUntil(a.eventDate) - daysUntil(b.eventDate));
}

interface RemindersState {
  hydrated: boolean;
  reminders: Reminder[];
  hydrate: () => Promise<void>;
  addReminder: (reminder: Reminder) => Promise<void>;
  toggle: (id: string) => Promise<void>;
  /** Remove every reminder tied to a policy — used when the policy is deleted. */
  removeForPolicy: (policyId: string) => Promise<void>;
}

async function persistAndSync(reminder: Reminder, all: Reminder[]) {
  const db = await getDb();
  await db.upsert(TABLES.reminders, reminder.id, reminder);
  // fire-and-forget: notification sync must never block the UI
  syncReminderNotifications(all).catch(() => {});
}

export const useRemindersStore = create<RemindersState>((set, get) => ({
  hydrated: false,
  reminders: [],

  hydrate: async () => {
    const db = await getDb();
    const reminders = (await db.list<Reminder>(TABLES.reminders)).sort(
      (a, b) => daysUntil(a.eventDate) - daysUntil(b.eventDate),
    );
    set({ hydrated: true, reminders });
    syncReminderNotifications(reminders).catch(() => {});
  },

  addReminder: async (reminder) => {
    const reminders = [...get().reminders, reminder].sort(
      (a, b) => daysUntil(a.eventDate) - daysUntil(b.eventDate),
    );
    set({ reminders });
    await persistAndSync(reminder, reminders);
  },

  toggle: async (id) => {
    const target = get().reminders.find((r) => r.id === id);
    if (!target) return;
    const updated: Reminder = { ...target, enabled: !target.enabled, updatedAt: new Date().toISOString() };
    const reminders = get().reminders.map((r) => (r.id === id ? updated : r));
    set({ reminders });
    await persistAndSync(updated, reminders);
  },

  removeForPolicy: async (policyId) => {
    const doomed = get().reminders.filter((r) => r.policyId === policyId);
    if (doomed.length === 0) return;
    const reminders = get().reminders.filter((r) => r.policyId !== policyId);
    set({ reminders });
    const db = await getDb();
    await Promise.all(doomed.map((r) => db.remove(TABLES.reminders, r.id)));
    // re-sync notifications so a deleted policy's alerts disappear
    syncReminderNotifications(reminders).catch(() => {});
  },
}));
